<template>
    <div v-if="isVisible" class="pmp-gateway-widget border rounded px-3 py-2 my-2">
        <!-- Store selector (patient dashboard mode) -->
        <div v-if="allowStoreSelect" class="d-flex align-items-center mb-1" style="gap: 6px;">
            <span class="font-weight-bold" style="font-size: 0.875em; white-space: nowrap;">Store:</span>
            <b-form-select
                v-model="selectedStoreId"
                :options="storeOptions"
                size="sm"
                style="max-width: 260px;"
                :disabled="fetchingScores || loadingStores"
                @change="onStoreChange" />
            <b-spinner v-if="loadingStores" small class="ml-1" />
        </div>

        <!-- Initial score load spinner -->
        <div v-if="fetchingScores" class="d-flex align-items-center text-muted" style="font-size: 0.875em;">
            <b-spinner small class="mr-2" />
            Loading PMP data&hellip;
        </div>

        <!-- Error fetching scores -->
        <b-alert v-else-if="fetchError" variant="warning" show class="mb-0 py-1 px-2" style="font-size: 0.875em;">
            <i class="fas fa-exclamation-triangle mr-1" />{{ fetchError }}
        </b-alert>

        <!-- Loaded state -->
        <template v-else-if="patientResult">
            <!--
                Single flat flex container. In wide mode all items sit on one line.
                In narrow mode (@container) the label group breaks to its own row,
                each badge takes ~50% width, and the report button spans the full width.
            -->
            <div class="pmp-result">
                <!-- Label + refresh — always kept together -->
                <div class="pmp-label-group">
                    <span class="font-weight-bold" style="font-size: 0.875em;">PMP:</span>
                    <b-button
                        size="sm"
                        variant="link"
                        class="text-muted p-0 ml-1"
                        :disabled="fetchingScores"
                        v-b-tooltip.hover
                        title="Refresh PMP scores"
                        @click="fetchPatientScores">
                        <i class="fas fa-sync-alt" style="font-size: 0.875em;" />
                    </b-button>
                </div>

                <!-- NARx scores -->
                <template v-if="patientResult.narxScores && patientResult.narxScores.length > 0">
                    <b-badge
                        v-for="score in patientResult.narxScores"
                        :key="score.scoreType"
                        :variant="scoreBadgeVariant(score.scoreValue)"
                        class="pmp-badge px-2 py-1">
                        {{ score.scoreType }}: {{ score.scoreValue }}
                    </b-badge>
                </template>

                <span v-else-if="!patientResult.success" class="text-warning" style="font-size: 0.875em;">
                    {{ patientResult.errorMessage || 'No data available' }}
                </span>

                <span v-else class="text-muted" style="font-size: 0.875em;">No scores available</span>

                <!-- View Report button -->
                <b-button
                    v-if="patientResult.reportUrl"
                    class="pmp-report-btn"
                    size="sm"
                    variant="outline-info"
                    :disabled="loadingReport"
                    @click="handleViewReport">
                    <b-spinner v-if="loadingReport" small class="mr-1" />
                    <i v-else class="fas fa-external-link-alt mr-1" />
                    View PMP Report
                </b-button>
            </div>

            <!-- NARx warning / alert messages -->
            <div v-if="uniqueNarxMessages.length > 0" class="mt-1">
                <b-alert
                    v-for="(msg, i) in uniqueNarxMessages"
                    :key="i"
                    :variant="messageVariant(msg.severity)"
                    show
                    class="mb-1 py-1 px-2"
                    style="font-size: 0.8em;">
                    {{ msg.text }}
                </b-alert>
            </div>

            <!-- States that responded -->
            <div
                v-if="patientResult.responseStates && patientResult.responseStates.length > 0"
                class="text-muted mt-1"
                style="font-size: 0.75em;">
                States: {{ patientResult.responseStates.join(', ') }}
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import axios from 'axios';
import moment from 'moment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

const PMP_PERMISSION = 'Pharmacists/PMP Gateway';

interface PmpStore {
    id: number;
    name: string;
}

interface PmpScore {
    scoreType: string;
    scoreValue: string;
}

interface PmpMessage {
    type: string | null;
    text: string | null;
    severity: string | null;
}

interface PmpPatientResult {
    success: boolean;
    errorMessage: string | null;
    narxScores: PmpScore[];
    narxMessages: PmpMessage[];
    reportUrl: string | null;
    reportExpiration: string | null; // ISO datetime string
    responseStates: string[];
}

@Component
export default class PmpGatewayWidget extends Vue {
    /** Full patient object. The widget extracts what it needs internally. */
    @Prop({ required: true }) patient!: any;

    /**
     * Store ID to use as the location context for PMP requests.
     * On the prescription verification page this comes from prescription.storeID.
     * Ignored when allowStoreSelect is true (the widget resolves the store itself).
     */
    @Prop({ default: null }) storeId!: number | null;

    /**
     * When true, shows a store selector so the user can pick the location context.
     * Used on the patient dashboard where no single prescription store is implied.
     * The widget defaults to the patient's most recently processed prescription store.
     */
    @Prop({ default: false }) allowStoreSelect!: boolean;

    configured = false;
    fetchingScores = false;
    loadingReport = false;
    loadingStores = false;
    fetchError: string | null = null;
    patientResult: PmpPatientResult | null = null;

    stores: PmpStore[] = [];
    selectedStoreId: number | null = null;

    // -----------------------------------------------------------------------
    // Computed
    // -----------------------------------------------------------------------

    get hasPermission(): boolean {
        return (this.$permissions as any[]).some(
            (p: any) => p.permissionCodeName === PMP_PERMISSION
        );
    }

    get isVisible(): boolean {
        return this.configured && this.hasPermission;
    }

    /** Derived patient fields extracted from whatever patient shape is passed in. */
    get patientFirstName(): string {
        return this.patient?.firstName || this.patient?.FirstName || '';
    }

    get patientLastName(): string {
        return this.patient?.lastName || this.patient?.LastName || '';
    }

    get patientBirthdate(): string {
        const dob = this.patient?.dateOfBirth || this.patient?.DateOfBirth;
        if (!dob) return '';
        const m = moment(String(dob).substring(0, 10), 'YYYY-MM-DD');
        if (!m.isValid()) return '';
        return m.format('YYYY-MM-DD');
    }

    get patientZipCode(): string | null {
        const addresses: any[] = this.patient?.addresses || this.patient?.Addresses || [];
        const primary = addresses.find((a: any) => a.isPrimary || a.IsPrimary) ?? addresses[0];
        return primary?.postalCode || primary?.PostalCode || null;
    }

    get patientPhone(): string | null {
        const raw: string = this.patient?.phoneNumber || this.patient?.PhoneNumber || '';
        const digits = raw.replace(/\D/g, '');
        return digits.length >= 10 ? digits.slice(-10) : (digits || null);
    }

    get patientSex(): string | null {
        const s = this.patient?.sex || this.patient?.Sex || '';
        if (!s) return null;
        const u = s.toUpperCase();
        if (u === 'M' || u === 'MALE') return 'M';
        if (u === 'F' || u === 'FEMALE') return 'F';
        return 'U';
    }

    get canSearch(): boolean {
        if (this.allowStoreSelect && !this.selectedStoreId) return false;
        return !!(this.patientFirstName && this.patientLastName && this.patientBirthdate &&
            (this.patientZipCode || this.patientPhone));
    }

    get uniqueNarxMessages() {
        const seen = new Set<string>();
        return (this.patientResult?.narxMessages ?? []).filter(m => {
            const key = m.text ?? '';
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }

    get storeOptions() {
        return [
            { value: null, text: '— Select a store —' },
            ...this.stores.map(s => ({ value: s.id, text: s.name })),
        ];
    }

    // -----------------------------------------------------------------------
    // Lifecycle
    // -----------------------------------------------------------------------

    async created() {
        await this.checkStatus();
        if (!this.isVisible) return;

        if (this.allowStoreSelect) {
            await this.loadStoresAndLastStore();
        } else {
            this.selectedStoreId = this.storeId;
        }

        if (this.canSearch) {
            await this.fetchPatientScores();
        }
    }

    @Watch('patient.id')
    async onPatientChanged() {
        this.patientResult = null;
        this.fetchError = null;
        if (!this.isVisible) return;

        if (this.allowStoreSelect) {
            await this.loadStoresAndLastStore();
        }

        if (this.canSearch) {
            await this.fetchPatientScores();
        }
    }

    @Watch('storeId')
    onStoreIdPropChanged(val: number | null) {
        if (!this.allowStoreSelect) {
            this.selectedStoreId = val;
        }
    }

    // -----------------------------------------------------------------------
    // Methods
    // -----------------------------------------------------------------------

    async checkStatus() {
        try {
            const resp = await axios.get<{ configured: boolean; hasPermission: boolean }>(
                '/api/PmpGateway/status'
            );
            this.configured = resp.data.configured;
        } catch {
            this.configured = false;
        }
    }

    async loadStoresAndLastStore() {
        this.loadingStores = true;
        try {
            const [storesResp, lastStoreResp] = await Promise.all([
                axios.get<PmpStore[]>('/api/PmpGateway/stores'),
                this.patient?.id
                    ? axios.get<{ storeId: number | null }>(`/api/PmpGateway/patient-last-store/${this.patient.id}`)
                    : Promise.resolve(null),
            ]);
            this.stores = storesResp.data;
            this.selectedStoreId = lastStoreResp?.data?.storeId ?? null;
        } catch {
            // Non-fatal: the widget still works, scores will use appsettings location fallback.
        } finally {
            this.loadingStores = false;
        }
    }

    async onStoreChange() {
        this.patientResult = null;
        this.fetchError = null;
        if (this.canSearch) {
            await this.fetchPatientScores();
        }
    }

    async fetchPatientScores() {
        if (!this.isVisible || !this.canSearch) return;

        this.fetchingScores = true;
        this.fetchError = null;

        try {
            const resp = await axios.post<PmpPatientResult>('/api/PmpGateway/patient-request', {
                firstName: this.patientFirstName,
                lastName: this.patientLastName,
                birthdate: this.patientBirthdate,
                zipCode: this.patientZipCode,
                phone: this.patientPhone,
                sexCode: this.patientSex,
                storeId: this.selectedStoreId,
            });
            this.patientResult = resp.data;
        } catch (err: any) {
            this.fetchError = err?.response?.data ?? 'Failed to load PMP data.';
        } finally {
            this.fetchingScores = false;
        }
    }

    /**
     * Opens the PMP report in a new tab.
     *
     * window.open() is called SYNCHRONOUSLY inside the click handler before any
     * await, which satisfies browser popup-blocker heuristics. We then write a
     * loading page into the tab and navigate it to the real URL once retrieved.
     */
    async handleViewReport() {
        if (!this.patientResult?.reportUrl) return;

        // Must be synchronous — called directly from the user click event.
        const newTab = window.open('', '_blank');
        if (!newTab) {
            this.$notification(NotificationOptions.error(
                'Popups are blocked. Please allow popups for this site to view the PMP report.'
            ));
            return;
        }

        // Show a loading indicator in the newly opened tab.
        newTab.document.write(
            '<!DOCTYPE html><html><head><title>PMP Report Loading\u2026</title>' +
            '<style>body{font-family:sans-serif;display:flex;align-items:center;' +
            'justify-content:center;height:100vh;margin:0;flex-direction:column;}' +
            '.spin{border:4px solid #eee;border-top:4px solid #3498db;border-radius:50%;' +
            'width:40px;height:40px;animation:s 1s linear infinite;margin-bottom:16px;}' +
            '@keyframes s{to{transform:rotate(360deg);}}</style></head>' +
            '<body><div class="spin"></div><p>Loading PMP report&hellip;</p></body></html>'
        );
        newTab.document.close();

        this.loadingReport = true;

        try {
            let reportUrl = this.patientResult.reportUrl;

            // If the cached report URL has expired, do a fresh patient request first.
            if (this.isReportExpired()) {
                const refreshed = await this.refreshPatientRequest();
                if (!refreshed || !this.patientResult?.reportUrl) {
                    newTab.close();
                    return;
                }
                reportUrl = this.patientResult.reportUrl;
            }

            // Exchange the ViewableReportUrl for the one-time GET link.
            const resp = await axios.post<{ reportLink: string }>('/api/PmpGateway/report', {
                reportUrl,
                storeId: this.selectedStoreId,
            });

            newTab.location.href = resp.data.reportLink;
        } catch (err: any) {
            newTab.close();
            this.$notification(NotificationOptions.error(
                err?.response?.data ?? 'Failed to retrieve the PMP report.'
            ));
        } finally {
            this.loadingReport = false;
        }
    }

    isReportExpired(): boolean {
        if (!this.patientResult?.reportExpiration) return true;
        return new Date(this.patientResult.reportExpiration) <= new Date();
    }

    async refreshPatientRequest(): Promise<boolean> {
        try {
            const resp = await axios.post<PmpPatientResult>('/api/PmpGateway/patient-request', {
                firstName: this.patientFirstName,
                lastName: this.patientLastName,
                birthdate: this.patientBirthdate,
                zipCode: this.patientZipCode,
                phone: this.patientPhone,
                sexCode: this.patientSex,
                storeId: this.selectedStoreId,
            });
            this.patientResult = resp.data;
            return true;
        } catch (err: any) {
            this.$notification(NotificationOptions.error(
                err?.response?.data ?? 'Failed to refresh PMP data.'
            ));
            return false;
        }
    }

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    scoreBadgeVariant(scoreValue: string): string {
        const val = parseInt(scoreValue, 10);
        if (isNaN(val)) return 'secondary';
        if (val >= 700) return 'danger';
        if (val >= 400) return 'warning';
        return 'success';
    }

    messageVariant(severity: string | null): string {
        if (!severity) return 'info'
        const s = severity.toLowerCase();
        if (s === 'high' || s === 'critical' || s === 'error') return 'danger';
        if (s === 'medium' || s === 'warning' || s === 'warn') return 'warning';
        return 'info';
    }
}
</script>

<style scoped>
.pmp-gateway-widget {
    background-color: #f8f9fa;
    container-type: inline-size;
}

.pmp-result {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.pmp-label-group {
    display: flex;
    align-items: center;
}

/* Narrow layout: label owns its row, scores go 2-per-row, button is full-width */
@container (max-width: 550px) {
    .pmp-label-group {
        flex: 0 0 100%;
    }

    .pmp-badge {
        flex: 1 0 calc(50% - 3px);
        text-align: center;
    }

    .pmp-report-btn {
        flex: 0 0 100%;
    }
}
</style>
